// /me/assignments — My Assignments view per spec §5 + frontend-design
// §3 MyAssignmentsList. Three groups (Active, Upcoming, Past); URL state
// for derived_status + cursor; cursor-reset on filter change (F-004).

import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { meApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { useUrlState } from '@/utils/useUrlState';
import {
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
  DerivedStatusBadge,
} from '@/components/primitives';
import {
  formatCalendarDate,
  formatCurrencyString,
  formatPercentOneDecimal,
} from '@/utils/format';
import { ApiError } from '@/types/errors';
import type { DerivedAssignmentStatus, MyAssignment } from '@/types/domain';

const ALL_STATUSES: DerivedAssignmentStatus[] = ['active', 'upcoming', 'past'];

export function MyAssignmentsPage() {
  const url = useUrlState();
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => h1Ref.current?.focus(), []);

  const statusFilter = url.getAll('derived_status') as DerivedAssignmentStatus[];
  const effectiveStatus =
    statusFilter.length > 0 ? statusFilter : (['active', 'upcoming'] as DerivedAssignmentStatus[]);
  const cursor = url.get('cursor');

  const queryArgs = useMemo(
    () => ({
      derived_status: effectiveStatus,
      cursor: cursor ?? undefined,
    }),
    [effectiveStatus, cursor],
  );

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: qk.myAssignments(queryArgs),
    queryFn: () => meApi.myAssignments(queryArgs),
  });

  const groups = useMemo(() => {
    const out: Record<DerivedAssignmentStatus, MyAssignment[]> = {
      active: [],
      upcoming: [],
      past: [],
    };
    for (const a of data?.data ?? []) {
      out[a.derived_status].push(a);
    }
    return out;
  }, [data]);

  const filterToggle = (s: DerivedAssignmentStatus) => {
    const set = new Set(effectiveStatus);
    if (set.has(s)) set.delete(s);
    else set.add(s);
    const arr = Array.from(set);
    url.setFilterOrSort({ derived_status: arr.length ? arr : null });
  };

  const showEmpty =
    !isPending && !isError && (data?.data.length ?? 0) === 0;

  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          My Assignments
        </h1>
      </header>

      <div className="filter-bar" role="region" aria-label="Filters">
        <fieldset className="filter-group">
          <legend>Show</legend>
          {ALL_STATUSES.map((s) => (
            <label key={s} style={{ marginRight: 12, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={effectiveStatus.includes(s)}
                onChange={() => filterToggle(s)}
              />{' '}
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </label>
          ))}
        </fieldset>
      </div>

      {isPending ? (
        <LoadingSkeleton />
      ) : isError ? (
        <ErrorBanner
          message="We couldn't load your assignments."
          requestId={error instanceof ApiError ? error.requestId : undefined}
          onRetry={() => void refetch()}
        />
      ) : showEmpty ? (
        statusFilter.length > 0 ? (
          <EmptyState
            message="No assignments match these filters."
            action={
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => url.clearAll()}
              >
                Clear filters
              </button>
            }
          />
        ) : (
          <EmptyState message="You have no assignments." />
        )
      ) : (
        <>
          {ALL_STATUSES.map((s) =>
            groups[s].length > 0 ? (
              <Group key={s} status={s} rows={groups[s]} />
            ) : null,
          )}
          <Pagination
            nextCursor={data?.next_cursor ?? null}
            loading={isFetching}
            onNext={() => {
              if (data?.next_cursor) url.setCursor(data.next_cursor);
            }}
          />
        </>
      )}
    </div>
  );
}

function Group({
  status,
  rows,
}: {
  status: DerivedAssignmentStatus;
  rows: MyAssignment[];
}) {
  const heading = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <section className="section">
      <header className="section-header">
        <h2>{heading}</h2>
        <span className="muted">{rows.length} assignment{rows.length === 1 ? '' : 's'}</span>
      </header>
      <table className="data-table">
        <caption className="visually-hidden">{heading} assignments</caption>
        <thead>
          <tr>
            <th scope="col">Project</th>
            <th scope="col">Client</th>
            <th scope="col">Role</th>
            <th scope="col">Allocation</th>
            <th scope="col">Bill rate</th>
            <th scope="col">Start</th>
            <th scope="col">End</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <Link to={`/agreements/${r.agreement_id}`}>{r.agreement_project_name}</Link>
              </td>
              <td>{r.agreement_client_name}</td>
              <td>{r.role ?? '—'}</td>
              <td>{formatPercentOneDecimal(r.allocation_pct)}</td>
              <td>{formatCurrencyString(r.bill_rate)}</td>
              <td>{formatCalendarDate(r.start_date)}</td>
              <td>{formatCalendarDate(r.end_date)}</td>
              <td>
                <DerivedStatusBadge derivedStatus={r.derived_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function LoadingSkeleton() {
  return (
    <>
      {['Active', 'Upcoming', 'Past'].map((label) => (
        <section className="section" key={label} aria-label={`${label} assignments — loading`}>
          <header className="section-header">
            <h2>{label}</h2>
          </header>
          <Skeleton rows={3} height="32px" />
        </section>
      ))}
    </>
  );
}
